import {EnvironmentProvider, ErrorBoundary, useApi} from "@webng/react-app-common";
import globalSettings from "../globalSettings";
import React, {ForwardedRef, forwardRef, useEffect, useMemo, useState} from "react";
import {LiveblogTeaserWidgetInitialData} from "@webng-types/embedjs";
import {LoadErrorView} from "../LoadErrorView";
import {LiveblogTeaserWidgetApi, LiveblogTeaserWidgetProps} from "./LiveblogTeaserWidgetProps";
import {ApiClient, isApiResponseError} from "@webng/tickaroo-api";
import {LiveblogTeaserWidgetCore} from "./LiveblogTeaserWidgetCore";
import {I18nWrapper} from "../I18nWrapper";
import {sharedThemeConfigurationSchema} from "@webng-types/embedjs/shared";
import {liveblogTeaserThemeConfigurationSchema} from "@webng-types/embedjs/teaser";

interface LiveblogTeaserControllerProps extends LiveblogTeaserWidgetProps {
}

function LiveblogTeaserControllerImp({ clientId, themeId, liveblogId, overrideSharedThemeConfiguration, overrideLiveblogTeaserThemeConfiguration, disableTracking, liveblogUrl, title, initialData: ssrInitialData }: LiveblogTeaserControllerProps, ref: ForwardedRef<LiveblogTeaserWidgetApi>) {
  const apiClient = useApi()

  const [data, setData] = useState<LiveblogTeaserWidgetInitialData | undefined>(
    ssrInitialData?.gameShowResponse && ssrInitialData?.liveblogTeaserThemeConfiguration ? ssrInitialData : undefined
  )
  const [error, setError] = useState<number|undefined>(undefined)

  useEffect(() => {
    // If we already have full SSR data, skip the API fetch
    if (ssrInitialData?.gameShowResponse && ssrInitialData?.liveblogTeaserThemeConfiguration) {
      return;
    }

    const query: any = {client_id: clientId, themeId, liveblogId}

    if(!liveblogUrl) {
      query["includeLiveblogUrl"] = window.location.origin
    }
    if(overrideLiveblogTeaserThemeConfiguration.limit) {
      query["limit"] = overrideLiveblogTeaserThemeConfiguration.limit
    }
    if(overrideLiveblogTeaserThemeConfiguration.content) {
      query["content"] = overrideLiveblogTeaserThemeConfiguration.content
    }

    apiClient.getJson(ApiClient.API_EMBED_V4_LIVEBLOG_TEASER, query)
    .then((d: LiveblogTeaserWidgetInitialData) => {
          if(d.liveblogTeaserThemeConfiguration) {
            return d
          } else {
            return {
              ...d,
              LiveblogTeaserWidgetInitialData: liveblogTeaserThemeConfigurationSchema.parse(d.configuration),
              sharedThemeConfiguration: sharedThemeConfigurationSchema.parse(d.configuration)
            }
          }
        })
    .then(setData)
    .catch(e => {
      if(isApiResponseError(e)) {
        setError(e.status)
      } else {
        setError(0)
      }
    })
  }, [apiClient, clientId, themeId, liveblogId, liveblogUrl, overrideLiveblogTeaserThemeConfiguration.limit, overrideLiveblogTeaserThemeConfiguration.content, ssrInitialData])

  const [finalLiveblogTeaserThemeConfiguration, finalSharedThemeConfiguration] = useMemo(() => {
    if (data?.liveblogTeaserThemeConfiguration && data?.sharedThemeConfiguration) {

      const sharedConfiguration = sharedThemeConfigurationSchema.parse(Object.assign({}, data.sharedThemeConfiguration, overrideSharedThemeConfiguration))
      const liveblogTeaserConfiguration = liveblogTeaserThemeConfigurationSchema.parse(Object.assign({}, data.liveblogTeaserThemeConfiguration, overrideLiveblogTeaserThemeConfiguration))

      return [liveblogTeaserConfiguration, sharedConfiguration]
    } else {
      return [undefined, undefined]
    }
  }, [data?.liveblogTeaserThemeConfiguration, data?.sharedThemeConfiguration, overrideSharedThemeConfiguration, overrideLiveblogTeaserThemeConfiguration])


  if (error !== undefined) {
    return <LoadErrorView status={error} />
  } else if (finalLiveblogTeaserThemeConfiguration && finalSharedThemeConfiguration && data) {
    return <I18nWrapper locale={finalSharedThemeConfiguration.locale} gameShowResponse={data.gameShowResponse}>
      <LiveblogTeaserWidgetCore ref={ref}
                                initialData={{gameShowResponse: data.gameShowResponse!, uniqueId: data.uniqueId!}}
                                css={data.css || ""}
                                liveblogId={liveblogId}
                                themeId={themeId}
                                disableTracking={disableTracking}
                                liveblogUrl={liveblogUrl || data.gameShowResponse?.game?.canonical_url || data.liveblogUrl || ""}
                                title={title}
                                sharedThemeConfiguration={finalSharedThemeConfiguration}
                                liveblogTeaserThemeConfiguration={finalLiveblogTeaserThemeConfiguration} />
    </I18nWrapper>
  } else {
    return <React.Fragment />
  }
}


const LiveblogTeaserController = forwardRef(LiveblogTeaserControllerImp)

function LiveblogTeaserWidgetImp({clientId, ...props}: LiveblogTeaserWidgetProps, ref: ForwardedRef<LiveblogTeaserWidgetApi>) {
  return <React.StrictMode>
    <ErrorBoundary>
      <EnvironmentProvider apiHost={globalSettings.baseUrl} clientId={clientId}>
        <LiveblogTeaserController ref={ref} clientId={clientId} {...props} />
      </EnvironmentProvider>
    </ErrorBoundary>
  </React.StrictMode>
}


export const LiveblogTeaserWidget = forwardRef(LiveblogTeaserWidgetImp)
